/**
 * Submit-time helpers for useEventForm.js. Encapsulates:
 *   - normalizeCesionTemporalAsiento / syncCesionTemporalAsiento
 *   - procesarEditoresQuill (writes Quill content into localData)
 *   - crearEvento (normalizes localData into the emit('terminado') payload)
 *   - submit (orchestrates validate → fecha-error checks → cesión sync →
 *     editores → crearEvento)
 *
 * Extracted from useEventForm.js to keep each composable under the canonical
 * ~200 LOC ceiling.
 */

// Backend returns absolute URLs on GET but expects relative paths on
// POST/PATCH: it prepends the storage domain to whatever it receives, so
// re-submitting the GET URL would accumulate the domain on every save
// (https://storage.../https://storage.../images/...). Strip the domain
// here so the payload carries only the path the backend already has.
// `localData.image` stays the full URL for display — only the emitted
// payload is rewritten.
const STORAGE_DOMAIN = 'https://storage.compralaentrada.com';

// AvanzaEsp: reglas de negocio del vínculo con el partido. Módulo puro
// compartido con el formulario legacy para no duplicar el contrato.
import { normalizeEvoPayload } from '@/composables/admin/events/evoRules';
import cloneDeep from 'lodash/cloneDeep';

function stripStorageDomain(url) {
    if (!url) return url;
    return url.startsWith(STORAGE_DOMAIN + '/')
        ? url.substring(STORAGE_DOMAIN.length + 1)
        : url;
}

/**
 * Serializa el estado del formulario en el payload que espera el backend.
 *
 * Función PURA respecto a `localData`: trabaja sobre una copia y devuelve un
 * objeto nuevo. Antes estas normalizaciones se aplicaban directamente sobre
 * `localData` (que `useEventForm` devuelve públicamente), así que un fallo de red
 * o un segundo intento encontraban el formulario ya mutado: valores convertidos a
 * número, campos puestos a `null` y claves borradas. Serializar no debe destruir la
 * fuente de estado.
 *
 * @param {Object} localData            Estado reactivo del formulario (no se muta).
 * @param {Object} [originalEvento={}]  Evento original; sus claves se conservan siempre.
 * @returns {Object} Payload listo para emitir.
 */
function serializeEventoPayload(localData, originalEvento = {}) {
    // Copia profunda: las normalizaciones posteriores (y los `delete`) operan aquí,
    // nunca sobre el estado reactivo del formulario.
    const draft = cloneDeep(localData);

    // Extract status.id if it's an object, otherwise keep as-is
    if (draft.status && typeof draft.status === 'object') {
        draft.status = draft.status.id;
    }

    // Extract limite_tiempo.id if it's an object, otherwise keep as-is
    if (draft.limite_tiempo_cambiar_nombre_entrada && typeof draft.limite_tiempo_cambiar_nombre_entrada === 'object') {
        draft.limite_tiempo_cambiar_nombre_entrada = draft.limite_tiempo_cambiar_nombre_entrada.id;
    }

    // Clear cuenta_atras if venta_online_cerrada is false
    if (!draft.venta_online_cerrada) {
        draft.cuenta_atras = null;
    }

    // Handle recinto logic
    if (!draft.recinto || draft.recinto === 'false') {
        draft.place_obj = null;
        draft.place_id = null;
    } else {
        draft.entradas_compartidas = false;
        draft.eventos_compartidos = [];
    }

    // Clear SGA/LFP ids if experiencia: an experience event is not an
    // SGA/LFP event, so the payload must NOT carry sga_id/lfp_id/
    // sga_product_id (send them empty rather than stale). The watcher
    // (useEventFormWatchers) already cleared the form state; this covers
    // EDIT payloads loaded with experiencia=true.
    if (draft.experiencia) {
        draft.lfp_id = null;
        draft.sga_id = null;
        draft.sga_product_id = null;
    }

    // Process no_cerrar_butacas_combinaciones
    if (draft.no_cerrar_butacas_combinaciones?.length) {
        draft.descuentos_abonados_no_cerrar_butacas = draft.no_cerrar_butacas_combinaciones.flatMap(
            c => (c.descuentos || []).map(d => d.id)
        );
    } else {
        delete draft.descuentos_abonados_no_cerrar_butacas;
    }

    // AvanzaEsp (AvanzaEsp): exclusividad entre vincular un partido y crear
    // uno nuevo, y limpieza si el evento es una experiencia. El mismo módulo
    // puro que validaba el formulario legacy, para no duplicar la regla.
    const evoNormalized = normalizeEvoPayload(draft);
    draft.evo_match_id = evoNormalized.evo_match_id;
    draft.create_in_evo = evoNormalized.create_in_evo;

    // Clean up temp fields
    delete draft.descuentos_abonados_no_cerrar_butacas_seleccionados;
    delete draft.tipo_abono_no_cerrar_butacas;
    delete draft.no_cerrar_butacas_combinaciones;

    // Process tags
    // BaseMultiselect emits arrays of IDs (numbers) directly via
    // getOptionValue(), so `tags_seleccionadas[i]` is already the integer
    // we want to send. The legacy pattern of extracting `.id` is left as
    // a defensive fallback in case the field ever ends up populated
    // with full tag objects (e.g., when the API returns a hydrated
    // resource during EDIT — though today the API only returns ids).
    if (draft.tags_seleccionadas?.length) {
        draft.tags = draft.tags_seleccionadas.map(tag =>
            typeof tag === 'object' ? tag.id : tag
        );
    } else {
        draft.tags = [];
    }

    // Parse coste_cambio_nombre
    if (draft.coste_cambio_nombre) {
        draft.coste_cambio_nombre = parseFloat(draft.coste_cambio_nombre);
    }

    // Parse monedero amount/percent (decimals-only mode keeps them as
    // strings during typing; the backend expects numbers).
    if (draft.importe_monedero_abonado) {
        draft.importe_monedero_abonado = parseFloat(draft.importe_monedero_abonado);
    }
    if (draft.porcentaje_monedero_abonado) {
        draft.porcentaje_monedero_abonado = parseFloat(draft.porcentaje_monedero_abonado);
    }

    // Delete preventa fields if not configured
    if (!draft.preventa_configurada) {
        delete draft.registro_preventa_desde;
        delete draft.registro_preventa_hasta;
        delete draft.venta_preventa_desde;
        delete draft.venta_preventa_hasta;
    }

    // Convert jornada to Number
    if (draft.jornada) {
        draft.jornada = Number(draft.jornada);
    }

    // Send `image` as a relative path to the backend so the storage
    // domain isn't prepended on every save. `localData.image` keeps
    // the full URL for the preview — only the emit payload is rewritten.
    const payloadImage = stripStorageDomain(draft.image);

    // Strip fields that weren't in the original evento props and are
    // still at their default value. The old master form only added fields
    // dynamically when the user interacted with them; our composable
    // pre-declares ~50 defaults for Vue 2.7 reactivity. Sending all of
    // them causes the backend to reject unknown fields. Keeping only
    // fields the user actually modified (i.e. changed from default) or
    // fields that were already in the original evento brings the payload
    // in line with what the master form sends.
    const originalKeys = new Set(Object.keys(originalEvento));
    const cleanedPayload = {};
    for (const [key, value] of Object.entries(draft)) {
        // Always keep fields that were in the original evento props
        if (originalKeys.has(key)) {
            cleanedPayload[key] = value;
            continue;
        }
        // Keep fields that were modified from their default value
        if (value !== null && value !== false && !(Array.isArray(value) && value.length === 0)) {
            cleanedPayload[key] = value;
        }
    }

    // Ensure image rewrite applies on the cleaned payload
    if (payloadImage !== draft.image) {
        cleanedPayload.image = payloadImage;
    }

    return cleanedPayload;
}

export { serializeEventoPayload };
export function useEventFormSubmit(localData, {
    emit,
    cesionTemporalBloqueadaPor,
    originalEvento = {}
}) {
    function normalizeCesionTemporalAsiento() {
        const val = localData.permitir_cesion_temporal_asiento;
        localData.permitir_cesion_temporal_asiento = val === true || val === 1 || val === '1';
    }

    function syncCesionTemporalAsiento() {
        normalizeCesionTemporalAsiento();

        if (!localData.permitir_cesion_temporal_asiento) {
            return;
        }

        if (cesionTemporalBloqueadaPor.value) {
            localData.permitir_cesion_temporal_asiento = false;
        }
    }

    function procesarEditoresQuill(editorRefs) {
        const refMap = {
            editorSynopsis: 'synopsis',
            editorCondicionesAcreditaciones: 'condiciones_acreditaciones',
            editorUnderagePolicy: 'underage_policy',
            editorReglamento: 'reglamento',
            editorRegistroPreventa: 'texto_registro_preventa',
            editorCondicionesLegales: 'texto_condiciones_legales_preventa'
        };

        for (const [refName, fieldName] of Object.entries(refMap)) {
            if (editorRefs[refName]) {
                localData[fieldName] = editorRefs[refName].processAndGetHTML();
            }
        }
    }

    /**
     * Serializa el formulario y emite el payload. Delega en un serializador puro:
     * `localData` se copia y se normaliza la copia, así que un fallo de red o un
     * segundo intento NO encuentran el formulario mutado.
     */
    function crearEvento() {
        emit('terminado', serializeEventoPayload(localData, originalEvento));
    }

    // Cross-validation cliente (rango de abonado Desde/Hasta y
    // acompañantes min/max): la manejan las custom rules de vee-validate
    // aplicadas directamente a los BaseInputNumber (validateRangoDesde,
    // validateRangoHasta, validateAcompañantesMin, validateAcompañantesMax
    // en EventoAbonados.vue). `observerRef.validate()` dispara todas las
    // rules del form, así que con `isValid === false` ya tenemos cubierto
    // el bloqueo del submit sin lógica adicional aquí.
    /**
     * @param {Object} observerRef  ValidationObserver ref
     * @param {Object} editorRefs   Quill editor refs
     * @param {Object} [options]
     * @param {boolean} [options.extraValid=true]  Condición adicional que debe cumplirse
     *   para emitir. AvanzaEsp la usa: la selección del partido participa del
     *   gate (`isValid && !errorEvo`, como en el formulario legacy) en lugar de
     *   bloquear por su cuenta, para que el usuario vea TODOS los errores juntos.
     * @returns {Promise<boolean>} true si emitió el payload
     */
    async function submit(observerRef, editorRefs, { extraValid = true } = {}) {
        const isValid = await observerRef.validate();

        syncCesionTemporalAsiento();

        if (isValid && extraValid) {
            procesarEditoresQuill(editorRefs);
            crearEvento();
            return true;
        }
        return false;
    }

    return {
        normalizeCesionTemporalAsiento,
        syncCesionTemporalAsiento,
        procesarEditoresQuill,
        crearEvento,
        submit
    };
}